/* eslint-disable react/jsx-props-no-spreading */

import {
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import { Fragment, useState } from 'react';
import { Plus, VerticalEllipsis } from '../../icons/ContinuIcons';
import {
  Workflow,
  WorkflowResultGroup,
  WorkflowType,
} from '@/client/services/api/graphql/gql/graphql';

import AssignmentConfirmationModal from '@/client/components/admin/assignments/overlay/AssignmentConfirmationModal';
import AssignmentCsvDownloadButton from './buttons/AssignmentCsvDownloadButton';
import AssignmentEditNameModal from './overlay/AssignmentEditNameModal';
import { useAccessHelper } from '@/client/services/hooks/auth/useAccessHelper';
import { useAdminAssignmentsStore } from './state/adminAssignmentsStore';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useWorkflowActions } from '@/client/components/admin/assignments/hooks/useWorkflowActions';

interface AssignmentDetailsActionGroupProps {
  data: Workflow | WorkflowResultGroup;
  isComplete: boolean;
  isWorkflowResultGroup?: boolean;
}

export default function AssignmentDetailsActionGroup({
  data,
  isComplete,
  isWorkflowResultGroup = false,
}: AssignmentDetailsActionGroupProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setSelectedInstanceCreationData, setSelectedWorkflowDuplicateData } =
    useAdminAssignmentsStore();

  const queryClient = useQueryClient();
  const { isAllowed, allowance } = useAccessHelper();
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationModalType, setConfirmationModalType] = useState<
    'unassign' | 'remind' | 'markCompleted' | 'duplicate' | 'delete' | null
  >(null);
  const [editNameModalOpen, setEditNameModalOpen] = useState(false);

  const { workflows: workflowsAllowed } = allowance;

  const permissions = {
    edit: isAllowed(['admin', 'assignments', 'edit'], null, ['admin', 'creator', 'manager']),
    view: isAllowed(['admin', 'assignments', 'view'], null, ['admin', 'creator', 'manager']),
    archive: isAllowed(['admin', 'assignments', 'archive'], null, ['admin', 'creator', 'manager']),
    complete: isAllowed(['admin', 'assignments', 'complete'], null, [
      'admin',
      'creator',
      'manager',
    ]),
  };

  const { id } = data;

  const {
    markAssignmentsCompletedMutation,
    sendAssignmentsReminderMutation,
    archiveAssignmentsMutation,
    deactivateWorkflowMutation,
    activateWorkflowMutation,
  } = useWorkflowActions();

  const mutationPayload = isWorkflowResultGroup ? { groupIds: [id] } : { workflowIds: [id] };

  const archiveAction = () =>
    archiveAssignmentsMutation
      .mutateAsync(mutationPayload)
      .then(() => navigate('/admin/assignments/overview'));

  const actions = [
    {
      label:
        data.__typename === 'Workflow' && data.enabled
          ? t('admin.assignments.details.actions.deactivate')
          : t('admin.assignments.details.actions.activate'),
      onClick: () =>
        data.__typename === 'Workflow' && data.enabled
          ? deactivateWorkflowMutation.mutateAsync({
              workflowId: id,
              refetchAction: () =>
                queryClient.refetchQueries({
                  queryKey: ['getWorkflowDetailsViewData', id],
                }),
            })
          : activateWorkflowMutation.mutateAsync({
              workflowId: id,
              refetchAction: () =>
                queryClient.refetchQueries({
                  queryKey: ['getWorkflowDetailsViewData', id],
                }),
            }),
      render: data.__typename === 'Workflow' && data.settings.type !== WorkflowType.PointInTime,
    },

    {
      label: t('admin.assignments.details.actions.editName'),
      onClick: () => setEditNameModalOpen(true),
      render: data.settings.type === WorkflowType.PointInTime && !isWorkflowResultGroup,
    },
    {
      label: t('admin.assignments.details.actions.editDetails'),
      onClick: () =>
        isWorkflowResultGroup
          ? navigate(`/admin/assignments/edit/instance/${id}`)
          : navigate(`/admin/assignments/edit/${id}`),
      render:
        permissions.edit &&
        (isWorkflowResultGroup ||
          (data.settings.type !== WorkflowType.PointInTime && workflowsAllowed)),
    },
    {
      label: t('admin.assignments.details.actions.duplicate'),
      onClick: () => {
        setConfirmationModalType('duplicate');
        setConfirmationModalOpen(true);
      },
      render: !isWorkflowResultGroup,
    },
    {
      label: t('admin.assignments.details.actions.sendReminder'),
      onClick: () => {
        setConfirmationModalType('remind');
        setConfirmationModalOpen(true);
      },
      render: !isComplete && (permissions.edit || permissions.view),
    },
    {
      label: t('admin.assignments.details.actions.markComplete'),
      onClick: () => {
        setConfirmationModalType('markCompleted');
        setConfirmationModalOpen(true);
      },
      render: !isComplete && permissions.complete,
    },
    {
      label: t('admin.assignments.details.actions.unassign'),
      onClick: () => {
        setConfirmationModalType('unassign');
        setConfirmationModalOpen(true);
      },
      render: !isComplete && permissions.archive,
    },
  ];

  const { type } = data.settings;

  return (
    <>
      <HStack spacing={4}>
        {type === WorkflowType.PointInTime && !isWorkflowResultGroup && (
          <Button
            leftIcon={<Plus />}
            onClick={() => {
              if (!isWorkflowResultGroup) {
                setSelectedInstanceCreationData(data as Workflow);
              }

              navigate('/admin/assignments/create/instance', { state: { workflowId: id } });
            }}
          >
            {t('admin.assignments.newInstance')}
          </Button>
        )}

        {/* {!isWorkflowResultGroup && <AssignmentCsvDownloadButton />} */}

        <Menu>
          <MenuButton
            variant="roundNeutralIconButton"
            isDisabled={actions.every((action) => action.render === false)}
            as={IconButton}
            aria-label={`${t('admin.assignments.workflowActionsMenu.aria')}`}
            icon={<VerticalEllipsis transform="rotate(90deg)" />}
          />

          <MenuList
            paddingY={2}
            paddingX={4}
            border="1px solid"
            borderColor="warmNeutral.200"
            borderRadius="6px"
            minWidth={0}
            width="fit-content"
          >
            {actions.map((action, index) => {
              if (!action.render) return null;

              return (
                <Fragment key={action.label}>
                  <MenuItem
                    key={action.label}
                    fontSize="14px"
                    fontWeight={500}
                    borderRadius="4px"
                    onClick={action.onClick}
                    _hover={{ backgroundColor: 'warmNeutral.100' }}
                  >
                    {action.label}
                  </MenuItem>

                  {index !== actions.length - 1 && <MenuDivider color="warmNeutral.200" />}
                </Fragment>
              );
            })}
          </MenuList>
        </Menu>
      </HStack>

      {confirmationModalOpen && confirmationModalType !== null && (
        <AssignmentConfirmationModal
          isOpen={confirmationModalOpen && confirmationModalType !== null}
          onClose={() => {
            setConfirmationModalOpen(false);
            setConfirmationModalType(null);
          }}
          modalType={confirmationModalType}
          onSubmit={() => {
            if (confirmationModalType === 'delete') {
              archiveAction();
            }

            if (confirmationModalType === 'unassign') {
              archiveAction();
            }

            if (confirmationModalType === 'remind') {
              sendAssignmentsReminderMutation.mutateAsync(mutationPayload);
            }

            if (confirmationModalType === 'duplicate') {
              setSelectedWorkflowDuplicateData(data as Workflow);

              navigate('/admin/assignments/create', { state: { workflowId: id } });
            }

            if (confirmationModalType === 'markCompleted') {
              markAssignmentsCompletedMutation.mutateAsync({ workflowIds: [id] }).then(() => {
                setConfirmationModalOpen(false);
                queryClient.refetchQueries({ queryKey: ['getWorkflowDetailsViewData', id] });
              });
            }
          }}
        />
      )}

      {editNameModalOpen && (
        <AssignmentEditNameModal
          isOpen={editNameModalOpen}
          onClose={() => setEditNameModalOpen(false)}
          id={id}
          refetchAction={() =>
            queryClient.refetchQueries({ queryKey: ['getWorkflowDetailsViewData', id] })
          }
        />
      )}
    </>
  );
}
